import React, {useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import tinycolor from 'tinycolor2';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CustomInput from './CustomInput';
import CustomInputLabel from './CustomInputLabel';

import config from '../../config/config';
import {getDays} from '../../helpers/scheduling';

const getDayObjects = ({days, date}) => days.map((d, i) => {
  const selectedDayKey = date.format('ddd').toUpperCase();
  const selectedDayIndex = days.findIndex(d => d === selectedDayKey);
  const diff = (selectedDayIndex - i) * -1;
  const newDate = moment(date).add(diff, 'days');
  const formattedDay = d.charAt(0) + d.slice(1).toLowerCase();
  return {key: d, name: formattedDay, index: i, date: newDate, dayOfMonth: newDate.format('D')};
});

const handleClick = ({multi, setValue, onChange}) => (e, day, values) => {
  if (multi) {
    const next = values.includes(day.key)
      ? values.filter(k => k !== day.key)
      : [...values, day.key];
    setValue(next);
    onChange(e, next);
  } else {
    onChange(e, day.date.toDate());
  }
};

const DayTabs = props => {
  const {
    date: jsDate,
    days,
    onChange,
    error,
    helperText,
    multi,
    color,
    disabledDays = [],
  } = props;
  const [value, setValue] = useState(
    multi ? (props?.defaultValue || []) : (props.defaultValue || null)
  );
  const date = moment(jsDate);
  const day = date.format('ddd').toUpperCase();
  const dayObjects = getDayObjects({days, date});
  const onClick = handleClick({multi, setValue, onChange});
  return (
    <CustomInput>
      {!!props.label &&
        <CustomInputLabel required={!!props?.required}>
          {props.label}
        </CustomInputLabel>
      }
      <Stack direction='row' spacing={0.125} flexWrap={'wrap'}>
        {(dayObjects || []).map(d => {
          const isSelected = multi ? value.includes(d.key) : d.key === day;
          const isDisabled = disabledDays.includes(d.key);
          return (
            <Box
              key={d.key}
              style={{width: 45, cursor: isDisabled ? 'not-allowed' : 'pointer'}}
              sx={{'&:hover': {color: isDisabled ? 'gray.100' : `${color}.main`}}}
              onClick={e => !isDisabled && onClick(e, d, value)}
            >
              <Stack
                spacing={0.25}
                paddingBottom='4px'
                justifyContent='space-between'
                alignItems='center'
              >
                {!props.nameOnly &&
                  <Typography
                    variant='large600Tight'
                    align='center'
                    paddingBottom='0.25em'
                    color='text.light'
                  >
                    {d.name}
                  </Typography>
                }
                <Avatar
                  style={{
                    backgroundColor: (
                      isSelected ? (config()?.colors?.[color])
                      : isDisabled ? 'rgb(240,240,240)'
                      : tinycolor(config()?.colors?.[color]).lighten(25).toString()
                    ),
                  }}
                >
                  <Typography
                    variant='largeBold'
                    align='center'
                    lineHeight='normal'
                    style={{color: isSelected ? 'white' : 'black'}}
                  >
                    {!!props?.nameOnly ? d.name : d.date.format('D')}
                  </Typography>
                </Avatar>
              </Stack>
            </Box>
          );
        })}

        {
          error && 
            <Alert variant='outlined' severity='error'>
              {helperText || 'Please select a valid day.'}
            </Alert>
        }
      </Stack>
    </CustomInput>
  );
};

DayTabs.defaultProps = {
  date: moment(),
  days: getDays(),
  color: 'primary',
  multi: false,
};

DayTabs.propTypes = {
  days: PropTypes.arrayOf(PropTypes.string),
  date: PropTypes.object, // moment.js datetime
  onChange: PropTypes.func,
  multi: PropTypes.bool,
};

export default DayTabs;
